import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Stethoscope, CheckCircle2 } from 'lucide-react';
import { apiService, Provider, ProviderRole } from '../../services/apiService';
import PageHeader from './ui/PageHeader';
import StatusPill from './ui/StatusPill';
import EmptyState from './ui/EmptyState';

const ROLE_OPTIONS: Array<{ value: ProviderRole; label: string }> = [
  { value: 'dentist', label: 'Zahnarzt' },
  { value: 'hygienist', label: 'Zahnprophylaxe / Hygiene' },
];

const roleLabel = (r: ProviderRole | undefined) =>
  ROLE_OPTIONS.find(o => o.value === r)?.label ?? r ?? '—';

const emptyProvider: Partial<Provider> = {
  name: '',
  email: '',
  phone: '',
  specialization: '',
  role: 'dentist',
  google_calendar_id: '',
  is_active: true,
};

const inputClass =
  'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const ProvidersPage: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Provider> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getProviders(false);
      setProviders(r.data.data || []);
    } catch (e: any) {
      setError(e?.message || 'Behandler konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async () => {
    if (!editing) return;
    if (!editing.name || !editing.email) {
      setError('Name und E-Mail sind Pflicht');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing.id) {
        await apiService.updateProvider(editing.id, editing);
      } else {
        await apiService.createProvider(editing);
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p: Provider) => {
    if (!window.confirm(`Behandler „${p.name}" wirklich löschen?`)) return;
    try {
      await apiService.deleteProvider(p.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Stammdaten"
        title="Behandler"
        description="Team und Kalenderzuordnungen verwalten"
        actions={
          <button
            onClick={() => setEditing({ ...emptyProvider })}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
          >
            <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
            Neuer Behandler
          </button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-brand-text-muted">Lade…</div>
        ) : providers.length === 0 ? (
          <EmptyState
            icon={Stethoscope}
            title="Noch keine Behandler"
            description="Lege Behandler an, um Termine zuweisen zu können."
            action={
              <button
                onClick={() => setEditing({ ...emptyProvider })}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer"
              >
                <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
                Erste Person anlegen
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-brand-border">
              <thead className="bg-brand-surface-2/60">
                <tr>
                  <Th>Name</Th>
                  <Th>Rolle</Th>
                  <Th>E-Mail</Th>
                  <Th>Telefon</Th>
                  <Th>Anzeige-Spezialisierung</Th>
                  <Th>Google-Kalender</Th>
                  <Th>Status</Th>
                  <Th align="right"><span className="sr-only">Aktionen</span></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {providers.map(p => (
                  <tr key={p.id} className={`hover:bg-brand-surface-2/60 transition-colors ${p.is_active ? '' : 'opacity-60'}`}>
                    <td className="px-4 py-3 text-sm font-medium text-brand-text">{p.name}</td>
                    <td className="px-4 py-3 text-sm">
                      <StatusPill tone={p.role === 'hygienist' ? 'accent' : 'info'}>
                        {roleLabel(p.role)}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-3 text-sm text-brand-text">{p.email}</td>
                    <td className="px-4 py-3 text-sm text-brand-text">{p.phone}</td>
                    <td className="px-4 py-3 text-sm text-brand-text">{p.specialization}</td>
                    <td className="px-4 py-3 text-sm">
                      {p.google_calendar_id ? (
                        <span
                          title={p.google_calendar_id}
                          className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 font-medium"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                          verknüpft
                        </span>
                      ) : (
                        <span className="text-xs text-brand-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {p.is_active
                        ? <StatusPill tone="success" dot>Aktiv</StatusPill>
                        : <StatusPill tone="neutral">Inaktiv</StatusPill>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <IconButton onClick={() => setEditing({ ...p })} label="Bearbeiten" tone="primary">
                          <Pencil className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                        <IconButton onClick={() => onDelete(p)} label="Löschen" tone="danger">
                          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="bg-brand-surface text-brand-text rounded-xl shadow-xl border border-brand-border max-w-lg w-full p-6">
            <h3 className="text-base font-semibold mb-5 text-brand-text">
              {editing.id ? 'Behandler bearbeiten' : 'Neuer Behandler'}
            </h3>
            <div className="space-y-3">
              <Field label="Name *" value={editing.name || ''} onChange={v => setEditing({ ...editing, name: v })} />
              <Field label="E-Mail *" type="email" value={editing.email || ''} onChange={v => setEditing({ ...editing, email: v })} />
              <Field label="Telefon" value={editing.phone || ''} onChange={v => setEditing({ ...editing, phone: v })} />
              <Field label="Anzeige-Spezialisierung" value={editing.specialization || ''} onChange={v => setEditing({ ...editing, specialization: v })} />

              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">Rolle *</label>
                <select
                  value={editing.role || 'dentist'}
                  onChange={e => setEditing({ ...editing, role: e.target.value as ProviderRole })}
                  className={inputClass}
                >
                  {ROLE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-xs text-brand-text-muted mt-1">
                  Bestimmt, welche Behandlungen abgedeckt werden können (z. B. Zahnreinigung → Hygiene, Beratung → Zahnarzt).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">Google Calendar ID</label>
                <input
                  type="text"
                  value={editing.google_calendar_id || ''}
                  onChange={e => setEditing({ ...editing, google_calendar_id: e.target.value })}
                  placeholder="z. B. dra.garcia@seadent.es"
                  className={`${inputClass} font-mono`}
                />
                <p className="text-xs text-brand-text-muted mt-1">
                  Eigener Kalender. Service-Account muss Lesezugriff haben. Leer lassen, wenn Termine nur über das Admin gepflegt werden.
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.is_active}
                  onChange={e => setEditing({ ...editing, is_active: e.target.checked })}
                  className="rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                />
                <span>Aktiv</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-brand-border">
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium border border-brand-border rounded-md text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer"
              >
                Abbrechen
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              >
                {saving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th className={`px-4 py-3 text-${align} text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted`}>
    {children}
  </th>
);

const IconButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  tone: 'primary' | 'danger';
}> = ({ children, onClick, label, tone }) => {
  const toneClass =
    tone === 'danger'
      ? 'text-brand-text-muted hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 dark:hover:text-rose-400'
      : 'text-brand-text-muted hover:text-brand-primary hover:bg-brand-accent/10';
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${toneClass}`}
    >
      {children}
    </button>
  );
};

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; type?: string }> = ({
  label,
  value,
  onChange,
  type = 'text',
}) => (
  <div>
    <label className="block text-sm font-medium text-brand-text mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={inputClass}
    />
  </div>
);

export default ProvidersPage;
